// src/components/OrderStatusPage.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from 'react-router-dom';

const url = process.env.REACT_APP_SERVER_URL || "https://centralsoft.com.my";

// Helper functions
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

const safeToFixed = (value, decimals = 2) => {
  const num = safeNumber(value);
  return num.toFixed(decimals);
};

// Format date to dd/mm/yyyy for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Format date to yyyy-mm-dd for input (HTML date input requires this format)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Parse dd/mm/yyyy to Date object
const parseDateFromDisplay = (dateString) => {
  if (!dateString) return null;
  const [day, month, year] = dateString.split('/');
  return new Date(`${year}-${month}-${day}`);
};

const OrderStatusPage = () => {
  const stallID = localStorage.getItem("stallID");
  const [orders, setOrders] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  // Navigate back to other pages
  const navigateToCustomerMenu = () => {
    navigate('/ordering');
  };

  const navigateToKitchenDisplay = () => {
    navigate('/kitchen-display');
  };

  // Fetch all orders with their items and addons
  const fetchOrders = useCallback(async (page = 1, limit = itemsPerPage) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching orders for stall:', stallID);

      const params = {
        stallID,
        status: ['pending', 'preparing', 'ready', 'delivered', 'completed', 'cancelled'],
        page,
        limit
      };

      // Add date range if provided (convert to ISO format for backend)
      if (dateRange.startDate) {
        const startDate = parseDateFromDisplay(dateRange.startDate);
        if (startDate) {
          params.startDate = startDate.toISOString().split('T')[0]; // yyyy-mm-dd format for backend
        }
      }
      if (dateRange.endDate) {
        const endDate = parseDateFromDisplay(dateRange.endDate);
        if (endDate) {
          params.endDate = endDate.toISOString().split('T')[0]; // yyyy-mm-dd format for backend
        }
      }

      // Fetch orders with pagination and date filtering
      const ordersResponse = await axios.get(`${url}/api/admin/orders/pending`, { params });

      console.log('📦 Orders API Response:', ordersResponse.data);
      const ordersData = ordersResponse.data.orders || ordersResponse.data || [];
     // const totalOrders = ordersResponse.data.total || ordersData.length;

      // For each order, fetch its items and addons
      const ordersWithDetails = await Promise.all(
        ordersData.map(async (order) => {
          try {
            // Fetch order items
            const itemsResponse = await axios.get(`${url}/api/admin/order_items`, {
              params: { orderID: order.orderID }
            });

            const itemsData = itemsResponse.data.items || itemsResponse.data || [];

            // For each item, fetch its addons
            const itemsWithAddons = await Promise.all(
              itemsData.map(async (item) => {
                try {
                  // Fetch addons
                  const addonsResponse = await axios.get(`${url}/api/admin/order_item_addons`, {
                    params: { orderItemID: item.orderItemID }
                  });

                  const addonsData = addonsResponse.data.addons || addonsResponse.data || [];

                  // Calculate correct prices
                  const addonsTotal = addonsData.reduce((total, addon) =>
                    total + (safeNumber(addon.addonPrice) * safeNumber(addon.addonQuantity, 1)), 0
                  );

                  const quantity = safeNumber(item.quantity, 1);
                  const storedTotalPrice = safeNumber(item.totalPrice);
                  const correctBaseTotal = storedTotalPrice - addonsTotal;
                  const correctUnitPrice = correctBaseTotal / quantity;

                  return {
                    orderItemID: item.orderItemID,
                    foodName: item.foodName || "Unknown Item",
                    quantity: quantity,
                    unitPrice: correctUnitPrice,
                    totalPrice: correctBaseTotal,
                    notes: item.notes || "",
                    status: item.status || 'pending',
                    selectedAddons: addonsData.map(addon => ({
                      addonName: addon.addonName || "Unknown Addon",
                      addonQuantity: safeNumber(addon.addonQuantity, 1),
                      addonPrice: safeNumber(addon.addonPrice),
                      addonTotal: safeNumber(addon.addonPrice) * safeNumber(addon.addonQuantity, 1)
                    })),
                    addonsTotal: addonsTotal,
                    finalPrice: storedTotalPrice
                  };
                } catch (error) {
                  console.error('Error fetching addons for item:', error);
                  return {
                    ...item,
                    selectedAddons: [],
                    addonsTotal: 0,
                    finalPrice: safeNumber(item.totalPrice)
                  };
                }
              })
            );

            // Calculate order total
            const orderTotal = itemsWithAddons.reduce((total, item) => {
              return total + item.finalPrice;
            }, 0);

            return {
              orderID: order.orderID,
              stallID: order.stallID,
              tableNumber: order.tableNumber || 'Unknown',
              status: order.status || 'pending',
              orderDate: order.orderDate,
              time: new Date(order.orderDate).toLocaleTimeString('en-MY', {
                hour: '2-digit',
                minute: '2-digit'
              }),
              date: formatDateForDisplay(order.orderDate), // Use dd/mm/yyyy format
              items: itemsWithAddons,
              total: `RM ${safeToFixed(orderTotal)}`,
              originalTotal: orderTotal
            };
          } catch (error) {
            console.error('Error fetching order details:', error);
            return {
              ...order,
              items: [],
              total: `RM ${safeToFixed(order.total)}`
            };
          }
        })
      );

      // Sort by order date (newest first)
      ordersWithDetails.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
      setOrders(ordersWithDetails);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [stallID, dateRange, itemsPerPage]);

  useEffect(() => {
    fetchOrders(currentPage, itemsPerPage);

    // Refresh every 30 seconds
    const interval = setInterval(() => fetchOrders(currentPage, itemsPerPage), 30000);
    return () => clearInterval(interval);
  }, [fetchOrders, currentPage, itemsPerPage]);

  // Handle date range change with dd/mm/yyyy format
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;

    // For date inputs, we need to convert from yyyy-mm-dd to dd/mm/yyyy
    if (value) {
      const [year, month, day] = value.split('-');
      const formattedDate = `${day}/${month}/${year}`;
      setDateRange(prev => ({
        ...prev,
        [name]: formattedDate
      }));
    } else {
      setDateRange(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Apply date filter
  const applyDateFilter = () => {
    setCurrentPage(1); // Reset to first page when applying new filter
    fetchOrders(1, itemsPerPage);
  };

  // Clear date filter
  const clearDateFilter = () => {
    setDateRange({
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
    fetchOrders(1, itemsPerPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchOrders(1, newItemsPerPage);
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    fetchOrders(page, itemsPerPage);
  };

  // Cancel order
  const cancelOrder = async (orderID) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      await axios.put(`${url}/api/admin/orders/status`, {
        stallID,
        orderID,
        status: 'canceled'
      });

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.orderID === orderID
            ? { ...order, status: 'canceled' }
            : order
        )
      );

      alert('Order canceled successfully');
    } catch (error) {
      console.error('Error canceling order:', error);
      alert('Error canceling order: ' + (error.response?.data?.error || error.message));
    }
  };

  // Update order status
  const updateOrderStatus = async (orderID, newStatus) => {
    try {
      await axios.put(`${url}/api/admin/orders/status`, {
        stallID,
        orderID,
        status: newStatus
      });

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.orderID === orderID
            ? { ...order, status: newStatus }
            : order
        )
      );

      alert(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error updating status: ' + (error.response?.data?.error || error.message));
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'preparing': return 'info';
      case 'ready': return 'success';
      case 'completed': return 'secondary';
      case 'canceled': return 'danger';
      default: return 'secondary';
    }
  };

  // Filter orders (client-side filtering for status)
  const filteredOrders = activeFilter === 'all'
    ? orders
    : orders.filter(order => order.status === activeFilter);

  // Calculate pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading orders...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="bg-dark text-white p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-0">📊 Order Status Dashboard</h1>
            <p className="mb-0">Stall: <strong>{stallID}</strong> | Total Orders: {orders.length}</p>
          </div>
          <div>
            <button className="btn btn-primary me-2" onClick={() => fetchOrders(currentPage, itemsPerPage)}>
              🔄 Refresh
            </button>
            <button className="btn btn-info me-2" onClick={navigateToKitchenDisplay}>
              👨‍🍳 Kitchen Display
            </button>
            <button className="btn btn-light" onClick={navigateToCustomerMenu}>
              🍽️ Customer Menu
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">📅 Filter by Date Range (DD/MM/YYYY)</h5>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                name="startDate"
                value={dateRange.startDate ? formatDateForInput(dateRange.startDate) : ''}
                onChange={handleDateRangeChange}
                placeholder="DD/MM/YYYY"
              />
              {dateRange.startDate && (
                <small className="text-muted">Selected: {dateRange.startDate}</small>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                name="endDate"
                value={dateRange.endDate ? formatDateForInput(dateRange.endDate) : ''}
                onChange={handleDateRangeChange}
                placeholder="DD/MM/YYYY"
              />
              {dateRange.endDate && (
                <small className="text-muted">Selected: {dateRange.endDate}</small>
              )}
            </div>
            <div className="col-md-6">
              <button className="btn btn-primary me-2" onClick={applyDateFilter}>
                📊 Apply Date Filter
              </button>
              <button className="btn btn-outline-secondary" onClick={clearDateFilter}>
                🗑️ Clear Filter
              </button>
              {(dateRange.startDate || dateRange.endDate) && (
                <div className="mt-2">
                  <small className="text-muted">
                    Filtering: {dateRange.startDate || 'Any start'} to {dateRange.endDate || 'Any end'}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Filter */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h5 className="card-title mb-3 mb-md-0">Filter by Status</h5>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div>
                <label className="form-label me-2">Items per page:</label>
                <select
                  className="form-select form-select-sm d-inline-block w-auto"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
          </div>
          <div className="btn-group flex-wrap mt-2" role="group">
            <button
              className={`btn ${activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveFilter('all')}
            >
              All ({orders.length})
            </button>
            <button
              className={`btn ${activeFilter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => setActiveFilter('pending')}
            >
              Pending ({orders.filter(o => o.status === 'pending').length})
            </button>
            <button
              className={`btn ${activeFilter === 'preparing' ? 'btn-info' : 'btn-outline-info'}`}
              onClick={() => setActiveFilter('preparing')}
            >
              Preparing ({orders.filter(o => o.status === 'preparing').length})
            </button>
            <button
              className={`btn ${activeFilter === 'ready' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setActiveFilter('ready')}
            >
              Ready ({orders.filter(o => o.status === 'ready').length})
            </button>
            <button
              className={`btn ${activeFilter === 'completed' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setActiveFilter('completed')}
            >
              Completed ({orders.filter(o => o.status === 'completed').length})
            </button>
            <button
              className={`btn ${activeFilter === 'canceled' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => setActiveFilter('canceled')}
            >
              Canceled ({orders.filter(o => o.status === 'canceled').length})
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="row">
        {paginatedOrders.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-info text-center">
              <h4>No orders found</h4>
              <p>There are currently no {activeFilter !== 'all' ? activeFilter : ''} orders</p>
              {(dateRange.startDate || dateRange.endDate) && (
                <p>Try adjusting your date range filter</p>
              )}
            </div>
          </div>
        ) : (
          paginatedOrders.map(order => (
            <div key={order.orderID} className="col-12 mb-4">
              <div className={`card border-${getStatusBadgeColor(order.status)}`}>
                <div className={`card-header bg-${getStatusBadgeColor(order.status)} text-white d-flex justify-content-between align-items-center`}>
                  <div>
                    <h5 className="mb-0">
                      Order #{order.orderID} - Table {order.tableNumber}
                    </h5>
                    <small>{order.date} • {order.time}</small>
                  </div>
                  <div className="text-end">
                    <span className="badge bg-light text-dark fs-6">
                      {order.status.toUpperCase()}
                    </span>
                    <div className="mt-1 fw-bold">{order.total}</div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Order Items */}
                  {order.items.map(item => (
                    <div key={item.orderItemID} className="mb-3 pb-2 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {item.quantity}x {item.foodName}
                            <span className={`badge bg-${getStatusBadgeColor(item.status)} ms-2`}>
                              {item.status}
                            </span>
                          </h6>
                          <div className="text-muted small">
                            Base: RM {safeToFixed(item.unitPrice)} × {item.quantity} = RM {safeToFixed(item.totalPrice)}
                          </div>

                          {item.selectedAddons && item.selectedAddons.length > 0 && (
                            <div className="mt-1">
                              <small className="text-muted">Add-ons:</small>
                              {item.selectedAddons.map((addon, index) => (
                                <div key={index} className="ms-2">
                                  <small>+ {addon.addonQuantity}x {addon.addonName} (+RM {safeToFixed(addon.addonTotal)})</small>
                                </div>
                              ))}
                              <div className="ms-2 mt-1">
                                <small className="fw-bold">Addons Total: +RM {safeToFixed(item.addonsTotal)}</small>
                              </div>
                            </div>
                          )}

                          <div className="mt-1">
                            <small className="fw-bold text-success">
                              Item Total: RM {safeToFixed(item.finalPrice)}
                            </small>
                          </div>

                          {item.notes && (
                            <div className="mt-1">
                              <small className="text-muted">Note: {item.notes}</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Actions */}
                <div className="card-footer">
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Order Status: <strong>{order.status.toUpperCase()}</strong>
                    </small>
                    <div>
                      {/* Cancel Button - Show for pending/preparing orders */}
                      {(order.status === 'pending' || order.status === 'preparing') && (
                        <button
                          className="btn btn-danger btn-sm me-2"
                          onClick={() => cancelOrder(order.orderID)}
                        >
                          ❌ Cancel Order
                        </button>
                      )}

                      {/* Status Update Buttons */}
                      {order.status === 'pending' && (
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => updateOrderStatus(order.orderID, 'preparing')}
                        >
                          🍳 Start Preparing
                        </button>
                      )}
                      {order.status === 'preparing' && (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => updateOrderStatus(order.orderID, 'ready')}
                        >
                          ✅ Mark Ready
                        </button>
                      )}
                      {order.status === 'ready' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => updateOrderStatus(order.orderID, 'completed')}
                        >
                          🚚 Mark Completed
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav>
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Auto-refresh indicator */}
      <div className="text-center mt-4">
        <small className="text-muted">
          🔄 Auto-refreshing every 30 seconds • Showing {paginatedOrders.length} of {filteredOrders.length} orders • Page {currentPage} of {totalPages}
        </small>
      </div>
    </div>
  );
};

export default OrderStatusPage;
